import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CircularProgress,
  IconButton,
  Collapse,
  Alert,
  Tooltip,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import axios from 'axios';
import Auth, { API_URL } from '../../utils/auth';
import { formatDateWithTime } from '../../utils/dateFormat';
import './ZoomParticipants.css';

const MATCHED_PAGE_SIZE = 10;

const ZoomParticipants = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [participantsData, setParticipantsData] = useState(null);
  const [matchedData, setMatchedData] = useState(null);
  const [matchedPage, setMatchedPage] = useState(1);
  const [loadingParticipants, setLoadingParticipants] = useState(true);
  const [loadingMatched, setLoadingMatched] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [expandedMeetings, setExpandedMeetings] = useState({});
  const navigate = useNavigate();

  const workspaceId = Auth.getWorkspaceId();

  const fetchParticipants = useCallback(async (date) => {
    if (!workspaceId) return;
    setLoadingParticipants(true);
    setError(null);
    try {
      const dateStr = date.format('YYYY-MM-DD');
      const response = await axios.get(
        `${API_URL}/zoom/zoom-todays-participants`,
        { params: { workspaceId, targetDate: dateStr } }
      );
      setParticipantsData(response.data);
      const expanded = {};
      (response.data.meetings || []).forEach((m) => {
        expanded[m.uuid] = true;
      });
      setExpandedMeetings(expanded);
    } catch (err) {
      console.error('Error fetching participants:', err);
      setError(
        err.response?.data?.error || 'Failed to load participants. Please try again.'
      );
    } finally {
      setLoadingParticipants(false);
    }
  }, [workspaceId]);

  const fetchMatchedCalls = useCallback(async (page) => {
    if (!workspaceId) return;
    setLoadingMatched(true);
    try {
      const response = await axios.get(
        `${API_URL}/zoom/zoom-matched-calls`,
        { params: { workspaceId, page, limit: MATCHED_PAGE_SIZE } }
      );
      setMatchedData(response.data);
    } catch (err) {
      console.error('Error fetching matched calls:', err);
    } finally {
      setLoadingMatched(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchParticipants(selectedDate);
  }, [fetchParticipants, selectedDate]);

  useEffect(() => {
    fetchMatchedCalls(matchedPage);
  }, [fetchMatchedCalls, matchedPage]);

  const toggleMeeting = (uuid) => {
    setExpandedMeetings((prev) => ({ ...prev, [uuid]: !prev[uuid] }));
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins >= 60) {
      const hrs = Math.floor(mins / 60);
      const remainMins = mins % 60;
      return `${hrs}h ${remainMins}m`;
    }
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  const isDateDisabled = (date) => date.isAfter(dayjs(), 'day');

  const renderMatchBadge = (participant) => {
    if (participant.isInternalUser) {
      return <span className="match-badge internal">Internal User</span>;
    }
    if (!participant.matchedClient) {
      return <span className="match-badge no-match">No Match</span>;
    }
    const { matchedClient } = participant;
    const clientName =
      `${matchedClient.firstName || ''} ${matchedClient.lastName || ''}`.trim() ||
      'Unknown';
    const isEmail = matchedClient.matchMethod === 'email';
    return (
      <span className={`match-badge ${isEmail ? 'email-match' : 'name-match'}`}>
        <a
          href={`/client/${matchedClient.id}`}
          className="client-link"
          onClick={(e) => {
            e.preventDefault();
            navigate(`/client/${matchedClient.id}`);
          }}
        >
          {clientName}
        </a>
        <Tooltip title={isEmail ? 'Matched by email' : 'Matched by name'}>
          <span style={{ opacity: 0.7, fontSize: '11px' }}>
            ({matchedClient.matchMethod})
          </span>
        </Tooltip>
      </span>
    );
  };

  const renderParticipantsTab = () => {
    if (loadingParticipants) {
      return (
        <div className="zoom-participants-loading">
          <CircularProgress sx={{ color: '#6a5af9' }} />
          <p>Loading Zoom participants...</p>
        </div>
      );
    }

    if (!participantsData) return null;

    return (
      <>
        <div className="zoom-participants-date-row">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date"
              value={selectedDate}
              onChange={(val) => val && setSelectedDate(val)}
              shouldDisableDate={isDateDisabled}
              slotProps={{
                textField: { size: 'small', sx: { width: 180 } }
              }}
            />
          </LocalizationProvider>
          <IconButton
            onClick={() => fetchParticipants(selectedDate)}
            sx={{ color: '#6a5af9' }}
          >
            <RefreshIcon />
          </IconButton>
        </div>

        <div className="zoom-participants-summary">
          <div className="summary-card">
            <div className="summary-value">{participantsData.summary?.totalMeetings ?? 0}</div>
            <div className="summary-label">Meetings</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{participantsData.summary?.totalParticipants ?? 0}</div>
            <div className="summary-label">Total Participants</div>
          </div>
          <div className="summary-card matched">
            <div className="summary-value">{participantsData.summary?.matchedParticipants ?? 0}</div>
            <div className="summary-label">Matched to Clients</div>
          </div>
          <div className="summary-card unmatched">
            <div className="summary-value">{participantsData.summary?.unmatchedParticipants ?? 0}</div>
            <div className="summary-label">Unmatched</div>
          </div>
        </div>

        {(!participantsData.meetings || participantsData.meetings.length === 0) && (
          <div className="zoom-participants-empty">
            <h3>No recorded meetings found</h3>
            <p>
              There are no recorded Zoom meetings for{' '}
              {selectedDate.format('MMMM D, YYYY')}. Meetings must have cloud
              recording enabled to appear here.
            </p>
          </div>
        )}

        {participantsData.meetings?.map((meeting) => (
          <div className="meeting-card" key={meeting.uuid}>
            <div
              className="meeting-card-header"
              onClick={() => toggleMeeting(meeting.uuid)}
            >
              <div>
                <p className="meeting-topic">{meeting.topic}</p>
                <p className="meeting-meta">
                  {meeting.startTime
                    ? formatDateWithTime(meeting.startTime)
                    : 'Unknown time'}
                  {meeting.duration ? ` \u00B7 ${formatDuration(meeting.duration * 60)}` : ''}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="participant-count">
                  {meeting.participants?.length || 0} participant
                  {(meeting.participants?.length || 0) !== 1 ? 's' : ''}
                </span>
                {expandedMeetings[meeting.uuid] ? (
                  <ExpandLessIcon sx={{ color: '#999' }} />
                ) : (
                  <ExpandMoreIcon sx={{ color: '#999' }} />
                )}
              </div>
            </div>

            <Collapse in={!!expandedMeetings[meeting.uuid]}>
              {meeting.participants && meeting.participants.length > 0 ? (
                <table className="meeting-participants-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Joined</th>
                      <th>Duration</th>
                      <th>Client Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meeting.participants.map((p, idx) => (
                      <tr key={idx}>
                        <td style={{ fontWeight: 500 }}>{p.name || 'Unknown'}</td>
                        <td style={{ color: p.email ? '#333' : '#bbb' }}>{p.email || 'N/A'}</td>
                        <td>
                          {p.joinTime
                            ? new Date(p.joinTime).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })
                            : '-'}
                        </td>
                        <td>{formatDuration(p.duration)}</td>
                        <td>{renderMatchBadge(p)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>
                  No participant data available
                </div>
              )}
            </Collapse>
          </div>
        ))}
      </>
    );
  };

  const renderMatchedCallsTab = () => {
    if (loadingMatched) {
      return (
        <div className="zoom-participants-loading">
          <CircularProgress sx={{ color: '#6a5af9' }} />
          <p>Loading matched calls...</p>
        </div>
      );
    }

    if (!matchedData) return null;

    const { matchedCalls, totalMatched, totalAnalyzed, page, totalPages } = matchedData;

    return (
      <>
        <div className="zoom-participants-summary">
          <div className="summary-card matched">
            <div className="summary-value">{totalMatched}</div>
            <div className="summary-label">Total Calls Matched</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{totalAnalyzed}</div>
            <div className="summary-label">AI Analysis Complete</div>
          </div>
          <div className="summary-card unmatched">
            <div className="summary-value">{totalMatched - totalAnalyzed}</div>
            <div className="summary-label">Pending Analysis</div>
          </div>
        </div>

        {(!matchedCalls || matchedCalls.length === 0) ? (
          <div className="zoom-participants-empty">
            <h3>No matched calls found</h3>
            <p>
              No Zoom calls have been matched to clients yet. Run "Process Transcripts"
              from Zoom Management to match calls.
            </p>
          </div>
        ) : (
          <>
            <table className="meeting-participants-table matched-calls-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Email</th>
                  <th>Meeting ID</th>
                  <th>Processed</th>
                  <th>AI Analysis</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {matchedCalls.map((call) => {
                  const clientName =
                    `${call.client.firstName || ''} ${call.client.lastName || ''}`.trim() ||
                    'Unknown';
                  return (
                    <tr key={call.transcriptId}>
                      <td>
                        <a
                          href={`/client/${call.client.id}`}
                          className="client-link"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/client/${call.client.id}`);
                          }}
                          style={{ fontWeight: 500 }}
                        >
                          {clientName}
                        </a>
                      </td>
                      <td style={{ color: call.client.email ? '#333' : '#bbb' }}>
                        {call.client.email || 'N/A'}
                      </td>
                      <td>
                        <span className="meeting-id-cell" title={call.meetingId}>
                          {call.meetingId ? call.meetingId.substring(0, 12) + '...' : '-'}
                        </span>
                      </td>
                      <td>
                        {call.createdAt ? formatDateWithTime(call.createdAt) : '-'}
                      </td>
                      <td>
                        {call.callScore ? (
                          <Chip
                            label="Complete"
                            size="small"
                            sx={{
                              backgroundColor: '#e8f8ef',
                              color: '#1a9c4a',
                              fontWeight: 500,
                              fontSize: '11px'
                            }}
                          />
                        ) : (
                          <Chip
                            label="Pending"
                            size="small"
                            sx={{
                              backgroundColor: '#fef3e2',
                              color: '#c27a15',
                              fontWeight: 500,
                              fontSize: '11px'
                            }}
                          />
                        )}
                      </td>
                      <td style={{ fontWeight: 600 }}>
                        {call.callScore?.overallScore != null
                          ? `${call.callScore.overallScore}%`
                          : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="pagination-controls">
                <IconButton
                  onClick={() => setMatchedPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  sx={{ color: '#6a5af9' }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <span className="pagination-info">
                  Page {page} of {totalPages}
                </span>
                <IconButton
                  onClick={() => setMatchedPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  sx={{ color: '#6a5af9' }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </div>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <div className="zoom-participants-page">
      <div className="zoom-participants-header">
        <h2>Zoom Meeting Logs</h2>
      </div>

      {error && (
        <Alert severity="error" sx={{ marginBottom: 3 }}>
          {error}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, newVal) => setActiveTab(newVal)}
        sx={{
          marginBottom: 3,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px'
          },
          '& .Mui-selected': {
            color: '#6a5af9 !important'
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#6a5af9'
          }
        }}
      >
        <Tab label="Zoom Participants" />
        <Tab
          label={
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Matched Calls
              {matchedData && matchedData.totalMatched > 0 && (
                <Chip
                  label={matchedData.totalMatched}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '11px',
                    fontWeight: 600,
                    backgroundColor: '#e8f8ef',
                    color: '#1a9c4a'
                  }}
                />
              )}
            </span>
          }
        />
      </Tabs>

      {activeTab === 0 && renderParticipantsTab()}
      {activeTab === 1 && renderMatchedCallsTab()}
    </div>
  );
};

export default ZoomParticipants;
